import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  fetchInspirationSearchStatus,
  getInspirationKeywordList,
  getInspirationOutput,
  getInspirationVideoCardList,
  submitInspirationSearch,
} from "./request";
import { create } from "zustand";
import { useRequest } from "ahooks";
import { WorkflowStatus } from "../interface";
import { format, subDays } from "date-fns";
import { searchEntityV3 } from "@/04-drivers/dal/entities";
import { getValueByKeyV2 } from "@/03-presenters/shared/helper";
import { parseAsString, useQueryState } from "nuqs";
import { GenericError } from "@/04-drivers/dal/error";
import { paymentModalStore } from "@/app/[locale]/tiktok/payment-modal";
import { useEffect, useMemo, useState } from "react";
import { InspirationKeywordType } from "./interface";
import { useUserInfo } from "../../hooks";
import { usePathname, useRouter } from "next/navigation";

export function useInspirationVideoCardList(keyword: string | undefined) {
  const [isEnd, setIsEnd] = useState(false);
  const { isHighLevelUser } = useUserInfo();
  const { relatedVideoIdList } = useInspirationSearch();
  const showPaymentModal = paymentModalStore((s) => s.setIsOpen);
  const [sortKey] = useQueryState(
    "sortKey",
    parseAsString.withDefault(
      "tiktok_material_search_material_video_total_gmv",
    ),
  );
  const { data, isFetching, isLoading, fetchNextPage, hasNextPage, error } =
    useInfiniteQuery({
      queryKey: [
        "inspirationVideoCardList",
        keyword,
        relatedVideoIdList,
        sortKey,
      ],
      retry: false,
      enabled: !!relatedVideoIdList.length,
      queryFn: ({ pageParam = 0 }) => {
        if (relatedVideoIdList.length === 0) {
          return [];
        }
        return getInspirationVideoCardList({
          limit: 10,
          offset: pageParam,
          // keyword_filter: keyword,
          entity_id_list: relatedVideoIdList,
          sort_filter: [
            {
              field_key: sortKey,
              order: 1,
            },
          ],
        });
      },
      getNextPageParam: (lastPage, allPages) => {
        return allPages.length * 10;
      },
      initialPageParam: 0,
    });

  useEffect(() => {
    if (
      error?.name === "OUT_OF_LIMIT_ERROR" ||
      error?.name === "UNAUTHORIZED_ERROR"
    ) {
      setIsEnd(true);
    }
  }, [error, showPaymentModal]);

  return { data, isFetching, isLoading, fetchNextPage, hasNextPage, isEnd };
}

interface InspirationSearchValue {
  keyword: string;
  hasProduct: boolean;
  hasSponsor: boolean;
  lastXDays: number | undefined;
  relatedVideoIdList: string[];
  dropDownLock: boolean;
}

interface InspirationSearchStore extends InspirationSearchValue {
  setKeyword: (keyword: string) => void;
  setHasProduct: (hasProduct: boolean) => void;
  setHasSponsor: (hasSponsor: boolean) => void;
  setLastXDays: (lastXDays: number | undefined) => void;
  setRelatedVideoIdList: (relatedVideoIdList: string[]) => void;
  reset: () => void;
  setDropDownLock: (dropDownLock: boolean) => void;
}

const initialValue: InspirationSearchValue = {
  keyword: "",
  hasProduct: false,
  hasSponsor: false,
  lastXDays: undefined,
  relatedVideoIdList: [],
  dropDownLock: false,
};

export const useInspirationSearch = create<InspirationSearchStore>((set) => ({
  ...initialValue,
  setKeyword: (keyword) => set({ keyword }),
  setHasProduct: (hasProduct) => set({ hasProduct }),
  setHasSponsor: (hasSponsor) => set({ hasSponsor }),
  setLastXDays: (lastXDays) => set({ lastXDays }),
  setRelatedVideoIdList: (relatedVideoIdList) => set({ relatedVideoIdList }),
  reset: () => set(initialValue),
  setDropDownLock: (dropDownLock) => set({ dropDownLock }),
}));

function getFieldFilter(
  hasProduct: boolean,
  hasSponsor: boolean,
  lastXDays: number | undefined,
  category?: string,
) {
  const fieldFilter = [];
  if (hasSponsor) {
    fieldFilter.push({
      filter_key: "video_material_search_sponsor_video",
      quick_option_name: "video_material_search_sponsor_video_1",
    });
  }
  if (hasProduct) {
    fieldFilter.push({
      filter_key: "video_material_search_product_video",
      quick_option_name: "video_material_search_product_video_1",
    });
  }
  if (lastXDays) {
    const today = new Date();
    const pastDate = subDays(today, lastXDays);

    fieldFilter.push({
      filter_key: "video_material_search_launch_time",
      left_date_time: format(pastDate, "yyyyMMdd"),
      right_date_time: format(today, "yyyyMMdd"),
    });
  }
  if (category) {
    fieldFilter.push({
      filter_key: "video_material_search_category",
      quick_option_name: category,
    });
  }
  return fieldFilter;
}

export function useSubmitInspirationSearch() {
  const router = useRouter();
  const pathname = usePathname();
  const { hasProduct, hasSponsor, lastXDays } = useInspirationSearch();
  const showPaymentModal = paymentModalStore((s) => s.setIsOpen);
  const { runAsync, loading } = useRequest(
    ({
      type,
      keyword,
      category,
    }: {
      type?: InspirationKeywordType;
      keyword?: string;
      category?: string;
    }) =>
      submitInspirationSearch({
        keyword,
        field_filter: getFieldFilter(
          hasProduct,
          hasSponsor,
          lastXDays,
          category,
        ),
        keyword_type: type,
      }),
    {
      manual: true,
      onError: (error) => {
        if (error instanceof GenericError) {
          if (
            error.name === "UNAUTHORIZED_ERROR" ||
            error.name === "OUT_OF_LIMIT_ERROR"
          ) {
            if (!pathname.includes("inspiration")) {
              router.push("/aiToolV2/inspiration");
            }

            setTimeout(() => {
              showPaymentModal({
                type: error.name as "UNAUTHORIZED_ERROR" | "OUT_OF_LIMIT_ERROR",
                open: true,
              });
            }, 0);
          }
        }
      },
    },
  );
  return { runAsync, loading };
}

export function useFetchInspirationSearchStatus(
  taskId: string,
  onSuccess?: () => void,
) {
  const { relatedVideoIdList, setRelatedVideoIdList } = useInspirationSearch();
  const { data, cancel, loading, run } = useRequest(
    () => fetchInspirationSearchStatus(taskId),
    {
      onSuccess: (data) => {
        if (
          data?.events?.find(
            (event) => event.event_type === WorkflowStatus.WORKFLOW_FINISHED,
          )
        ) {
          onSuccess?.();
          cancel();
        }
        if (
          data?.intermediate_outputs?.retrieved_video_id_list &&
          relatedVideoIdList.length === 0
        ) {
          setRelatedVideoIdList(
            data?.intermediate_outputs?.retrieved_video_id_list,
          );
        }
      },
      pollingInterval: 1500,
      onError: (error) => {
        console.log(error);
        cancel();
      },
    },
  );
  return {
    events: data?.events?.filter((event) =>
      [WorkflowStatus.NODE_STARTED, WorkflowStatus.NODE_FINISHED].includes(
        event.event_type,
      ),
    ),
    intermediateOutputs: data?.intermediate_outputs,
    loading,
    run,
    inputs: data?.inputs,
  };
}

export function useFetchInspirationOutput(taskId: string, taskDone: boolean) {
  const { data, isFetching } = useQuery({
    queryKey: ["inspirationOutput", taskId],
    queryFn: () => getInspirationOutput(taskId),
    enabled: taskDone,
  });
  return { data, loading: isFetching };
}

export function useMediaInfo({ idList }: { idList: string[] }) {
  const { data, isLoading } = useQuery({
    queryKey: ["mediaInfo", idList],
    enabled: !!idList.length,
    staleTime: 1000 * 60 * 60 * 12,
    queryFn: async () => {
      const res = await searchEntityV3(
        {
          entity_id_list: idList,
        },
        "",
        "entity/tiktok/material/detail/s3",
      );
      const mediaInfoList = res?.entity_list?.map((entity) => {
        const userAvatar = getValueByKeyV2(
          entity,
          "tiktok_material_detail_s3_user_avatar",
        )?.url as string | undefined;
        const videoCover = getValueByKeyV2(
          entity,
          "tiktok_material_detail_s3_video_cover",
        )?.url as string | undefined;
        return {
          userAvatar,
          id: entity.entity_id,
          videoCover,
        };
      });
      return mediaInfoList;
    },
  });
  return { data, isLoading };
}

export function useInspirationSearchList() {
  const [isSearching, setIsSearching] = useState(false);
  const { data, loading, run } = useRequest(
    async (keyword: string) => {
      if (!keyword) {
        return {
          brands: [],
          products: [],
          categories: [],
        };
      }
      const res = await getInspirationKeywordList(keyword);
      return res;
    },
    {
      manual: true,
    },
  );

  const debouncedRun = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (keyword: string) => {
      setIsSearching(true);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        try {
          await run(keyword);
        } finally {
          setIsSearching(false);
        }
      }, 500);
    };
  }, [run]);

  return { data, loading: loading || isSearching, debouncedRun, run };
}
